import {
  Box,
  DataTableColDef,
  GridCellParams,
  GridColumnVisibilityModel,
  GridSortItem,
  GridSortModel,
  MuiEvent,
  styled,
} from '@chronosphereio/chrono-ui';
import { GetFormattedDateArgs, getFormattedDateTimeString } from '@chronosphereio/core';
import { merge, sortBy } from 'lodash';
import { useMemo } from 'react';
import {
  AGGREGATIONS_CONFIG,
  AggregationTransform,
  DisplayValue,
  formatValue,
  getCalculations,
  Labels,
  Override,
  RawTransform,
  TableOverrides,
  TablePanelPropsBase,
  TableSort,
  TableSpec,
  TableTransform,
  TimeSeries,
  TransformKind,
  UnitOptions,
} from '@/core';
import { BasePanelProps, GlobalFilter } from '../../../plugin-system';
import { normalizeValueFieldName } from './utils/normalize';

export type TableRow = Record<string, string | number | null | undefined> & {
  // This id is  a reserved field.
  // It is used by the MUI Data Grid to identify each row internally
  // Avoid using `id` as the key, as the data might include `id` column with duplicate values
  _vfId: string;
};

/**
 * Types for the table that are only used in the `Viz` variant.
 *
 * @see {@link TablePropsBase} for types defined in the schema because they are also shared with
 * the `DashboardPanel` variant.
 */
interface TablePropsVizOnly {
  onRowClick?: (rowId: string, row: TableRow, e: MuiEvent) => void;

  /**
   * Callback of filters to be added. When this is provided, `String` type columns will render filters buttons on hover.
   */
  onAddFilters?: (filters: GlobalFilter[]) => void;
  compact?: boolean;

  /**
   * Optional boolean to disable CSV export.
   * @default true
   */
  disableCsvExport?: boolean;
}

export interface TableProps extends BasePanelProps<TableSpec>, TablePanelPropsBase, TablePropsVizOnly {
  // Extend values from `TablePanelPropsBase` below, as needed, to add in jsdoc to help developers.
}

export const DEFAULT_TRANSFORM: TableTransform = {
  kind: TransformKind.Aggregation,
  labelsToColumns: true,
  stats: ['Min', 'Max', 'First', 'Last', 'Mean', 'Sum'],
};

export const DEFAULT_UNIT: UnitOptions = { kind: 'Decimal', abbreviate: true };

const DEFAULT_COL_WIDTH = 200;
export const DEFAULT_TABLE_HEIGHT = 300;

export type TransformType = TableTransform['kind'];

export const NO_TRANSFORM_OPTION = { key: 'default', label: 'Default' };

export const TABLE_FORMAT_OPTIONS: Array<{ key: TransformType; label: string }> = [
  {
    key: TransformKind.Aggregation,
    label: 'Series To Rows',
  },
  {
    key: TransformKind.SeriesToColumns,
    label: 'Series To Columns',
  },
  {
    key: TransformKind.Raw,
    label: 'Raw',
  },
];

function getLabelKeysFromTimeSeries(data?: TimeSeries[]) {
  if (!data) {
    return [];
  }
  const allKeys = data.map((s) => Object.keys(s.labels || {}));
  const uniqueKeys = [...new Set(allKeys.flat())];
  return uniqueKeys;
}

const DEFAULT_UNIT_OPTION: UnitOptions = { kind: 'Decimal', abbreviate: true };

export function getNumberRenderer(units?: UnitOptions) {
  const renderNumber = (params: GridCellParams) => {
    if (typeof params?.value !== 'number' || params.value === null || params.value === undefined) return '--';
    return (
      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {formatValue(params.value, units ?? DEFAULT_UNIT_OPTION)}
      </Box>
    );
  };
  return renderNumber;
}

export function getIconTextRenderer(displayProcessor: (value: unknown) => DisplayValue) {
  const renderIconText = (params: GridCellParams) => {
    const { icon: Icon, text } = displayProcessor(params.value);

    if (!params?.value) return;
    return (
      <IconTextCell>
        <IconTextCellIconWrapper>{Icon}</IconTextCellIconWrapper>
        <IconTextCellText>{text}</IconTextCellText>
      </IconTextCell>
    );
  };

  return renderIconText;
}

const IconTextCell = styled(Box)(({ theme }) => {
  return {
    gap: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
  };
});
const IconTextCellIconWrapper = styled(Box)(({ theme }) => {
  return {
    // Keep icon aligned to top, so it doesn't hop to the middle when the content is expanded.
    alignSelf: 'flex-start',
    display: 'inline-flex',
    // Avoid the icon height impacting the layout beyond the lineHeight of the text. This avoids the cell
    // shifting a little when the cell is expanded and needs to rely on padding to maintain the layout of the
    // content.
    maxHeight: theme.typography.body2.lineHeight,
    // Center the content, so it's laid out properly despite the height limitation.
    alignItems: 'center',
  };
});
const IconTextCellText = styled(Box)(() => {
  return {
    overflow: 'hidden',
    alignItems: 'center',
    textOverflow: 'ellipsis',
  };
});

const NUMBER_WIDTH = 80;

type TransformOptions = {
  unit?: UnitOptions;
} & GetTimestampColumnOpts;

export function transformTimeSeriesToAggregationTable(
  data: TimeSeries[],
  transform: AggregationTransform,
  options?: TransformOptions
) {
  const { unit } = options || {};
  let columns: DataTableColDef[] = [];

  const labelKeys = getLabelKeysFromTimeSeries(data);
  const renderNumber = getNumberRenderer(unit);

  if (transform.labelsToColumns) {
    labelKeys.forEach((key) => {
      columns.push({
        field: key,
        headerName: key,
        width: DEFAULT_COL_WIDTH,
        flex: 1,
      });
    });
  } else {
    columns.push({
      field: 'metric',
      headerName: 'Metric',
      flex: 1,
    });
  }

  // Translate transform.stats into columns
  let statsColumns: DataTableColDef[] = [];
  if (transform.stats) {
    statsColumns = transform.stats.reduce((columns, stat) => {
      const config = AGGREGATIONS_CONFIG[stat];
      if (config) {
        columns.push({
          field: stat,
          headerName: config.label,
          width: NUMBER_WIDTH,
          flex: 1,
          renderCell: renderNumber,
          type: 'number',
        });
      }
      return columns;
    }, [] as DataTableColDef[]);
  }

  columns = columns.concat(statsColumns);

  const rows: TableRow[] = data.map((s) => {
    const calculations = getCalculations(s.values, transform.stats ?? []);

    return merge(
      {
        ...getLabelsFieldsIfEnabled(s.labels || {}, transform.labelsToColumns || false),
        _vfId: s.formattedName || s.name,
        metric: s.formattedName || s.name,
      },
      calculations
    );
  });
  return { columns, rows };
}

export function transformTimeSeriesToSeriesToColumns(data: TimeSeries[], options: TransformOptions) {
  const { unit, ...timestampColumnOpts } = options;
  const firstTimeSeries = data[0];
  const renderNumber = getNumberRenderer(unit);
  const columns: DataTableColDef[] = [getTimestampColumn(timestampColumnOpts)];
  const rows: TableRow[] = [];

  firstTimeSeries?.values.forEach((v, idx) => {
    const row = {
      _vfId: String(idx),
      time: v[0],
    };
    rows.push(row);
  });

  data.forEach((s) => {
    columns.push({
      field: s.name,
      flex: 1,
      headerName: s.name || s.formattedName,
      renderCell: renderNumber,
      type: 'number',
    });
    s.values.forEach((v, idx) => {
      const row = rows[idx];
      if (row) {
        row[s.name || ''] = v[1];
      }
    });
  });
  return { rows, columns };
}

export function transformTimeSeriesToRawTable(
  data: TimeSeries[],
  transform: RawTransform,
  seriesIndex = 0,
  options: TransformOptions
) {
  if (data.length === 0) {
    return { rows: [], columns: [] };
  }
  const { unit, ...timestampColumnOpts } = options;
  const renderNumber = getNumberRenderer(unit);
  const series = data[seriesIndex];
  const labelKeys = getLabelKeysFromTimeSeries(series ? [series] : undefined);
  const additionalColumns: DataTableColDef[] = [];

  if (transform.labelsToColumns) {
    labelKeys.forEach((key) => {
      additionalColumns.push({
        field: key,
        headerName: key,
        width: DEFAULT_COL_WIDTH,
      });
    });
  }
  const columns: DataTableColDef[] = [
    getTimestampColumn(timestampColumnOpts),
    ...additionalColumns,
    {
      field: 'value',
      headerName: 'Value',
      renderCell: renderNumber,
      type: 'number',
    },
  ];
  const rows: TableRow[] = [];
  series?.values.forEach((v, idx) => {
    rows.push({
      _vfId: String(idx),
      time: v[0],
      value: v[1],
      ...getLabelsFieldsIfEnabled(series.labels || {}, transform.labelsToColumns || false),
    });
  });
  return { rows, columns };
}

const getLabelsFieldsIfEnabled = (labels: Labels, enableLabelsToColumns: boolean) => {
  if (!enableLabelsToColumns) {
    return {};
  }
  return labels;
};

export function useTransformedTimeSeries(
  data: TimeSeries[],
  transform: TableTransform,
  selectedSeriesIdx: number,
  options: TransformOptions
): { rows: TableRow[]; columns: DataTableColDef[] } {
  return useMemo(() => {
    // Don't show columns if there is no data
    if (data.length === 0) {
      return { rows: [], columns: [] };
    }

    const normalizedTransformOptions = {
      ...options,
      unit: options.unit ?? DEFAULT_UNIT,
    };

    const isAggregation = transform.kind === TransformKind.Aggregation;
    const enableLabelsToColumns = isAggregation && transform.labelsToColumns;

    if (isAggregation) {
      return transformTimeSeriesToAggregationTable(data, transform as AggregationTransform, normalizedTransformOptions);
    } else if (transform.kind === TransformKind.SeriesToColumns) {
      return transformTimeSeriesToSeriesToColumns(data, normalizedTransformOptions);
    } else {
      const t: RawTransform = {
        kind: TransformKind.Raw,
        labelsToColumns: enableLabelsToColumns,
      };
      return transformTimeSeriesToRawTable(data, t, selectedSeriesIdx, normalizedTransformOptions);
    }
  }, [transform, data, selectedSeriesIdx, options]);
}

export const transformToGridSortModel = (sort?: TableSort[]): GridSortModel => {
  return sort?.map(({ sortBy, sortOrder }) => ({ field: sortBy, sort: sortOrder }) as GridSortItem) ?? [];
};

export const transformToColumnVisibilityModel = (
  overrides?: Array<Override<TableOverrides>>
): GridColumnVisibilityModel | undefined => {
  if (overrides === undefined) return undefined;

  const model: GridColumnVisibilityModel = {};
  for (let i = 0; i < overrides.length; i++) {
    const override = overrides[i];
    if (override && override.properties.hidden) {
      model[override.value] = false;
      const normalized = normalizeValueFieldName(override.value);
      if (normalized !== override.value) {
        model[normalized] = false;
      }
    }
  }

  return model;
};

/**
 * Sorts columns based on the order provided.
 */
export function sortColumns(columns: DataTableColDef[], order: string[] = []) {
  const normalizedOrder = order.map(normalizeValueFieldName);
  const sortedColumns = sortBy(columns, [
    (column) => {
      let orderNumber = order.indexOf(column.field);
      if (orderNumber === -1) {
        orderNumber = normalizedOrder.indexOf(normalizeValueFieldName(column.field));
      }
      return orderNumber === -1 ? order.length : orderNumber;
    },
  ]);
  return sortedColumns;
}

type GetTimestampColumnOpts = Pick<GetFormattedDateArgs, 'timeFormat' | 'timeZone'>;

/**
 * Returns the table column definition for the timestamp column. Ensures the time is formatted
 * using the standard formatting utils with the time zone abbreviation omitted to save space.
 */
export function getTimestampColumn({ timeFormat, timeZone }: GetTimestampColumnOpts): DataTableColDef {
  return {
    field: 'time',
    headerName: 'Time',
    // Hardcoded to a width that works well with our new time zone formats.
    // We need a little extra space for the 12h format because the AM/PM takes up extra space.
    width: timeFormat === '24h' ? 150 : 170,
    valueFormatter: ({ value }) => {
      if (typeof value === 'number') {
        return getFormattedDateTimeString({
          date: value,
          timeFormat,
          timeZone,
          // Omitting the time zone to save space.
          omitTimeZoneAbbreviation: true,
        });
      }

      return value;
    },
  };
}
